import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getTeamSettings, updateTeamSettings } from "services/team";
import Toast from "components/common/Toast";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const getTeamId = () => {
  try {
    return JSON.parse(localStorage.getItem("selectedTeam") || "{}")
      ?.currentTeamId;
  } catch {
    return undefined;
  }
};

const getIsAdmin = () => {
  try {
    return (
      JSON.parse(localStorage.getItem("selectedTeam") || "{}")?.currentRole ===
      "Admin"
    );
  } catch {
    return false;
  }
};

const ApiSettings = () => {
  const [citationsApiKey, setCitationsApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyTimeout = useRef<any>(null);

  // API key is editable by admins only; others see it read-only (like Model).
  const isAdmin = getIsAdmin();

  // Load the team's current API keys.
  useEffect(() => {
    const teamId = getTeamId();
    if (!teamId) return;
    (async () => {
      try {
        const res: any = await getTeamSettings(teamId);
        const settings = res?.data ?? res;
        if (typeof settings?.citations_api_key === "string") {
          setCitationsApiKey(settings.citations_api_key);
        }
      } catch (error) {
        console.error("Error fetching API settings:", error);
      }
    })();
  }, []);

  useEffect(() => () => clearTimeout(copyTimeout.current), []);

  const handleCopy = async () => {
    if (!citationsApiKey) return;
    try {
      await navigator.clipboard.writeText(citationsApiKey);
      setCopied(true);
      clearTimeout(copyTimeout.current);
      copyTimeout.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      Toast.error("Error", "Could not copy to clipboard");
    }
  };

  const handleSave = async () => {
    const teamId = getTeamId();
    if (!teamId) return;
    setIsSaving(true);
    try {
      const res: any = await updateTeamSettings({
        team_id: teamId,
        citations_api_key: citationsApiKey,
      });
      // Reflect the persisted value returned by the API.
      const saved = res?.data ?? res;
      if (typeof saved?.citations_api_key === "string") {
        setCitationsApiKey(saved.citations_api_key);
      }
      Toast.success("Success", "API settings saved");
    } catch (error) {
      console.error("Error saving API settings:", error);
      Toast.error("Error", "Failed to save API settings");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="mt-5 max-w-[720px]">
      <p className="text-sm text-[#475467] mb-6">
        Manage the keys used to authenticate with iPvijil APIs.
      </p>

      {/* Citations API */}
      <div className="border border-[#EAECF0] rounded-lg p-5 mb-6">
        <h5 className="text-sm text-[#344054] font-interSemiBold pb-0.5">
          Citations API
        </h5>
        <p className="text-sm text-[#475467]">
          Use this key to authenticate requests to the Citations API. Send it in
          the{" "}
          <span className="font-interMedium text-[#101828]">X-API-Key</span>{" "}
          request header.{" "}
          <Link
            to="/api-reference"
            className="text-[#1751D0] hover:underline font-interMedium"
          >
            View API Reference
          </Link>
        </p>

        <label className="text-sm font-interMedium text-[#344054] mt-4 mb-1.5 block">
          API Key
        </label>
        <div
          className={`flex items-center gap-x-1 border rounded-lg px-3 ${
            isAdmin ? "border-[#D0D5DD]" : "border-[#EAECF0] bg-[#F9FAFB]"
          }`}
        >
          <input
            type={showKey ? "text" : "password"}
            value={citationsApiKey}
            onChange={(e) => setCitationsApiKey(e.target.value)}
            placeholder="No API key set"
            readOnly={!isAdmin}
            className={`w-full py-2.5 text-sm bg-transparent outline-none font-mono tracking-wide ${
              isAdmin ? "text-[#101828]" : "text-[#475467] cursor-default"
            }`}
          />
          <button
            type="button"
            onClick={() => setShowKey((s) => !s)}
            className="text-[#667085] hover:text-[#1751D0] p-1 flex-shrink-0"
            title={showKey ? "Hide" : "Show"}
          >
            {showKey ? (
              <VisibilityOffIcon sx={{ width: 18, height: 18 }} />
            ) : (
              <VisibilityIcon sx={{ width: 18, height: 18 }} />
            )}
          </button>
          <button
            type="button"
            onClick={handleCopy}
            disabled={!citationsApiKey}
            className={`p-1 flex-shrink-0 ${
              citationsApiKey
                ? "text-[#667085] hover:text-[#1751D0]"
                : "text-[#D0D5DD] cursor-not-allowed"
            }`}
            title="Copy"
          >
            <ContentCopyIcon sx={{ width: 17, height: 17 }} />
          </button>
        </div>
        <p className="text-xs h-4 mt-1.5 text-[#1751D0]">
          {copied ? "Copied to clipboard" : ""}
        </p>
      </div>

      {isAdmin && (
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className={`submit-btn-blue !px-[15px] !text-sm hover:bg-[#120693] ${
            isSaving ? "button-disabled" : ""
          }`}
        >
          {isSaving ? "Saving..." : "Save"}
        </button>
      )}
    </div>
  );
};

export default ApiSettings;
